import React, { useCallback, useEffect, useMemo, useState } from "react";
import useFilters from "@/lib/useFilters";
import { PageHeader, Toast, Modal, EmptyState } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import TimeentriesFilters, { MONTHS } from "./TimeentriesFilters";
import TimeentriesCalendar from "./TimeentriesCalendar";
import TimeentriesValidationModal from "./TimeentriesValidationModal";
import TimeentriesExportDialog from "./TimeentriesExportDialog";
import TimeentriesCloseModal from "./TimeentriesCloseModal";
import "./timeentries.css";

const MAX_DAILY = 4;
const errText = (e) => e?.response?.data?.detail || "Eroare";

export default function TimeentriesPage() {
  const { hasRole, has } = useAuth();
  // fiecare buton după dreptul lui (Parametrii > Acces); validarea și închiderea lunii au efect doar pentru Admin/HR
  const su = hasRole("superadmin");
  const canClose = su || has("timeentries.close_month");
  const canExport = su || has("timeentries.export");
  const canValidate = su || has("timeentries.validate");
  const now = new Date();
  // filtre cu „Aplică filtre”: `filters` = cele aplicate (luna, structurile, categoriile, rapoartele); implicit: luna curentă, toate rapoartele
  const tf = useFilters({ year: now.getFullYear(), month: now.getMonth() + 1, structureIds: [], categories: [], employmentIds: [] });
  const filters = tf.applied;
  const [data, setData] = useState(null);           // răspunsul /timeentries/calendar
  const [entries, setEntries] = useState([]);       // copie locală, actualizată după fiecare salvare
  const [validations, setValidations] = useState([]);
  const [structures, setStructures] = useState([]);
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const [capAlert, setCapAlert] = useState(null);   // { total }
  const [validating, setValidating] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [closure, setClosure] = useState(null);       // { closed_at, closed_by_name } dacă luna e închisă
  const [closing, setClosing] = useState(false);       // fereastra de confirmare închide/redeschide

  const toast = (text, type = "info") => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "" }), 3500); };

  useEffect(() => { api.get("/mdm/org-units").then(r => setStructures((r.data || []).filter(u => u.active !== false))).catch(() => {}); }, []);

  const load = useCallback(async () => {
    try {
      const params = { year: filters.year, month: filters.month };
      const r = await api.get("/timeentries/calendar", { params });
      // structurile din răspuns completează lista generală (care poate lipsi pentru cine nu are dreptul s-o citească)
      setStructures(prev => { const m = new Map(prev.map(u => [u.id, u])); (r.data.structures || []).forEach(u => { if (u.active !== false && !m.has(u.id)) m.set(u.id, u); }); return [...m.values()]; });
      setData(r.data); setEntries(r.data.entries); setValidations(r.data.validations); setClosure(r.data.closure || null);
    } catch (e) { toast(errText(e), "error"); setData({ employments: [], holidays: [] }); }
  }, [filters.year, filters.month]);
  useEffect(() => { setData(null); load(); }, [load]);

  const structureNames = useMemo(() => Object.fromEntries(structures.map(s => [s.id, s.name])), [structures]);
  const entryIndex = useMemo(() => Object.fromEntries(entries.map(e => [`${e.employment_id}|${e.date}`, e])), [entries]);
  const validationIndex = useMemo(() => Object.fromEntries(validations.map(v => [v.employment_id, v])), [validations]);

  const allRows = useMemo(() => [...(data?.employments || [])]
    .sort((a, b) => a.name.localeCompare(b.name, "ro") || a.marca.localeCompare(b.marca, "ro")), [data]);
  // structurile alese includ și descendenții lor
  const structureSet = useMemo(() => {
    if (!filters.structureIds.length) return null;
    const set = new Set(filters.structureIds);
    let grew = true;
    while (grew) {
      grew = false;
      for (const s of structures) if (s.parent_id && set.has(s.parent_id) && !set.has(s.id)) { set.add(s.id); grew = true; }
    }
    return set;
  }, [filters.structureIds, structures]);
  const categories = useMemo(() => [...new Set(allRows.map(e => e.personnel_category).filter(Boolean))].sort((a, b) => a.localeCompare(b, "ro")), [allRows]);
  const filteredRows = allRows.filter(e => (!structureSet || structureSet.has(e.org_unit_id))
    && (!filters.categories.length || filters.categories.includes(e.personnel_category)));
  const rows = filters.employmentIds.length ? filteredRows.filter(e => filters.employmentIds.includes(e.id)) : filteredRows;
  const employmentOptions = filteredRows.map(e => ({ value: e.id, label: `${e.name} (${e.marca})` }));

  // Limita de 4h/zi e pe PERSOANĂ: se însumează toate rapoartele aceleiași persoane. Serverul rămâne autoritatea.
  const otherHoursSamePerson = (emp, day, exceptId) => entries
    .filter(e => e.date === day && e.id !== exceptId && allRows.find(r => r.id === e.employment_id)?.user_id === emp.user_id)
    .reduce((s, e) => s + e.hours, 0);

  const saveCell = async (emp, day, entry, value) => {
    if (Number.isNaN(value)) { toast("Introduceți un număr între 0 și 4 ore", "error"); return false; }
    try {
      if (value === null) {                                  // câmp golit => șterge pontajul zilei
        if (entry) { await api.delete(`/timeentries/${entry.id}`); setEntries(es => es.filter(e => e.id !== entry.id)); }
        return true;
      }
      const total = otherHoursSamePerson(emp, day, entry?.id) + value;
      if (total > MAX_DAILY) { setCapAlert({ total }); return false; }
      const r = entry ? await api.patch(`/timeentries/${entry.id}`, { hours: value })
                      : await api.post("/timeentries", { employment_id: emp.id, date: day, hours: value });
      setEntries(es => entry ? es.map(e => e.id === entry.id ? r.data : e) : [...es, r.data]);
      return true;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400 && /Limita/.test(errText(e))) setCapAlert({ total: null, text: errText(e) });
      else toast(errText(e), "error");
      if (status === 403) load();                            // luna a fost validată între timp
      return false;
    }
  };

  const confirmValidation = async () => {
    const { emp, validated } = validating;
    try {
      const r = await api.post(`/timeentries/${validated ? "unvalidate" : "validate"}`,
        { employment_id: emp.id, year: filters.year, month: filters.month });
      setValidations(vs => [...vs.filter(v => v.employment_id !== emp.id), r.data]);
      toast(validated ? "Pontaj deblocat" : "Pontaj validat", "success");
      setValidating(null);
    } catch (e) { toast(errText(e), "error"); }
  };

  const confirmClose = async () => {
    const reopen = !!closure;
    try {
      const r = await api.post(`/timeentries/${reopen ? "reopen-month" : "close-month"}`, { year: filters.year, month: filters.month });
      setClosing(false);
      if (reopen) { setClosure(null); toast("Pontaj redeschis", "success"); load(); return; }
      setClosure(r.data);
      toast(`Pontajul lunii ${MONTHS[filters.month - 1]} ${filters.year} a fost închis`, "success");
      // trecem la luna calendaristică următoare
      const next = filters.month === 12 ? { year: filters.year + 1, month: 1 } : { year: filters.year, month: filters.month + 1 };
      tf.setBoth({ ...next, employmentIds: [] });
    } catch (e) { toast(errText(e), "error"); setClosing(false); }
  };
  // Luna următoare e blocată până se închide luna curentă: serverul trimite ultima lună care se poate lucra.
  const hz = data?.horizon;
  const notReached = !!hz && (filters.year * 12 + filters.month) > (hz.year * 12 + hz.month);
  const fmtWhen = (iso) => { const d = new Date(iso); return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };

  return (
    <>
      <PageHeader title="Pontaj Proiecte" subtitle={`${MONTHS[filters.month - 1]} ${filters.year} — maximum ${MAX_DAILY} ore/zi pe persoană, luni–vineri`}
        actions={<>
          {canClose && data && (
            <button className={"btn " + (closure ? "btn-secondary" : "btn-danger")} onClick={() => setClosing(true)}
              disabled={notReached} title={notReached ? "Luna se poate închide după ce se închide luna dinaintea ei" : undefined} data-testid="te-close-month-btn">
              {closure ? "Redeschide pontajul" : "Închide pontajul"}
            </button>
          )}
          {canExport && <button className="btn btn-primary" onClick={() => setExporting(true)} data-testid="te-export-btn">Export</button>}
        </>} />
      <Toast msg={msg.text} type={msg.type} />
      <TimeentriesFilters f={tf} employmentOptions={employmentOptions}
        structures={structures} categories={categories} />

      {closure && (
        <div className="te-closed" data-testid="te-closed-banner">
          <b>Pontajul lunii {MONTHS[filters.month - 1]} {filters.year} este închis</b>
          {closure.closed_by_name ? ` de ${closure.closed_by_name}` : ""} la {fmtWhen(closure.closed_at)}. Nu se mai poate modifica nimic până la redeschidere.
        </div>
      )}
      {notReached && !closure && (
        <div className="te-wait" data-testid="te-wait-banner">
          <b>Luna {MONTHS[filters.month - 1]} {filters.year} este blocată</b> până se închide luna {String(hz.month).padStart(2, "0")}.{hz.year}. Lunile se închid în ordine, de HR sau Admin.
        </div>
      )}
      {data === null ? <div>Se încarcă...</div> : rows.length === 0 ? (
        <div className="card"><EmptyState text="Nu există rapoarte de muncă de afișat pentru filtrele alese." /></div>
      ) : (
        <TimeentriesCalendar year={filters.year} month={filters.month} rows={rows} entryIndex={entryIndex}
          validationIndex={validationIndex} holidays={data.holidays || []} structureNames={structureNames}
          canValidate={canValidate} closed={!!closure || notReached} onSaveCell={saveCell}
          onToggleValidation={(emp, validated) => setValidating({ emp, validated, name: emp.name })} />
      )}

      {capAlert && (
        <Modal title="Depășire limită zilnică" onClose={() => setCapAlert(null)} width={440}>
          <p style={{ fontSize: 14, color: "#334155", marginTop: 0 }} data-testid="te-cap-alert">
            {capAlert.total != null
              ? `Depășire limită zilnică. Maximum ${MAX_DAILY} ore/zi. Ați introdus ${capAlert.total} ore.`
              : capAlert.text}
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="btn btn-primary" onClick={() => setCapAlert(null)} data-testid="te-cap-ok">OK, corectez</button>
          </div>
        </Modal>
      )}
      {validating && (
        <TimeentriesValidationModal target={{ validated: validating.validated, name: validating.name }} month={filters.month}
          onClose={() => setValidating(null)} onConfirm={confirmValidation} />
      )}
      {closing && (
        <TimeentriesCloseModal reopen={!!closure} year={filters.year} month={filters.month}
          onClose={() => setClosing(false)} onConfirm={confirmClose} />
      )}
      {exporting && <TimeentriesExportDialog filters={filters} structures={structures} visibleIds={rows.length === allRows.length ? [] : rows.map(r => r.id)} onClose={() => setExporting(false)} />}
    </>
  );
}
